import { getElementIndex } from '~/utilities/arrayUtils'

import { AutoGeneratedAlbumFinished, DownloadAlbumFailed } from '../album/actions'
import { AlbumChangedToPrivate, AlbumLinkWasCopied } from '../albumOptions/actions'
import type { Action } from '../common/actions'
import { AlbumUnsubscriptionFailed } from '../currentUser/actions'
import {
    FilesCopiedToTimeline,
    FilesCopiedToTimelineFailed,
    FilesWereShared,
} from '../files/actions'
import {
    FileWasSetAsCoverPhoto,
    FilesCopiedToAlbum,
    FilesCopiedToAlbumFailed,
    FilesDeletionFailed,
    FilesDeletionSucceeded,
    FilesDownloadFailed,
    FilesRestorationFailed,
    FilesRestorationSucceeded,
    JobCopiedToTimeline,
    JobCopiedToTimelineFailed,
    JobDownloadFailed,
    SetFileAsCoverPhotoFailed,
    ShareCreationFailed,
    imageCopiedToClipboard,
    imageCopyToClipboardFailed,
} from '../job/actions'
import { JobDeletionFailed } from '../jobInfo/actions'
import {
    DeleteShareFailed,
    ShareCopiedToTimeline,
    ShareCopiedToTimelineFailed,
} from '../share/actions'
import {
    PlanCancelFailed,
    PlanCanceled,
    PlanChangeFailed,
    PlanChangeSucceeded,
    PlanReactivated,
    PlanReactivationFailed,
    PurchaseFailed,
    PurchaseSucceeded,
    StripeSetupIntentCreated,
} from '../storagePlan/actions'
import { MaxSelectionCountReached } from '../timeline/actions'
import {
    TrashAlbumsDeleteFailed,
    TrashAlbumsDeleteSuccess,
    TrashAlbumsRestoreFailed,
    TrashAlbumsRestoreSuccess,
    TrashFilesDeletionFailed,
    TrashFilesDeletionSucceeded,
} from '../trash/actions'
import {
    EmailSubscribeSettingsChanged,
    ProfileNameChangeFailed,
    ProfilePictureChangeFailed,
} from '../users/actions'

import {
    LongRunningTaskFinished,
    LongRunningTaskStarted,
    StatusNotificationDismissed,
} from './actions'

type StatusNotificationBase<T extends string> = { id: number; type: T }
export type PendingStatusType =
    | 'filesAreBeingDeleted'
    | 'filesAreBeingRestored'
    | 'filesAreBeingCopied'
    | 'preparingDownload'
type PendingNotification = StatusNotificationBase<PendingStatusType>

export type StatusNotification =
    | (StatusNotificationBase<'albumCoverPhotoSet'> & { relatedFile: FileID })
    | (StatusNotificationBase<'setAlbumCoverPhotoFailed'> & {
          relatedJobID: JobID
          relatedFile: FileID
      })
    | (StatusNotificationBase<'filesDeleted'> & {
          relatedJob: JobID
          relatedFiles: FileID[]
      })
    | (StatusNotificationBase<'filesDeletionFailed'> & {
          relatedJob: JobID
          relatedFiles: FileID[]
      } & { options?: { supressRetry?: boolean } })
    | (StatusNotificationBase<'trashFilesDeleted'> & { numFiles: number })
    | (StatusNotificationBase<'trashFilesDeletionFailed'> & {
          relatedFiles: FileID[]
      })
    | (StatusNotificationBase<'filesWereRestored'> & { numFiles: number })
    | (StatusNotificationBase<'filesRestorationFailed'> & {
          relatedJob: JobID
          relatedFiles: FileID[]
      })
    | (StatusNotificationBase<'autoCreatedAlbum'> & {
          relatedJobID: JobID
          relatedJobName: string
      })
    | (StatusNotificationBase<'deleteAlbumFailed'> & { relatedJobID: JobID })
    | (StatusNotificationBase<'unsubscribeAlbumFailed'> & {
          relatedJobID: JobID
      })
    | StatusNotificationBase<'jobCopiedToTimeline'>
    | StatusNotificationBase<'jobCopiedToTimelineFailed'>
    | (StatusNotificationBase<'filesCopiedToTimeline'> & {
          relatedFiles: FileID[]
      })
    | StatusNotificationBase<'filesCopiedToTimelineFailed'>
    | (StatusNotificationBase<'filesCopiedToAlbum'> & {
          relatedJob: JobID
          relatedFiles: FileID[]
          showAlbumLink: boolean
      })
    | (StatusNotificationBase<'filesCopiedToAlbumFailed'> & {
          relatedJob: JobID
          relatedFiles: FileID[]
      })
    | StatusNotificationBase<'shareCopiedToTimeline'>
    | StatusNotificationBase<'shareCopiedToTimelineFailed'>
    | StatusNotificationBase<'shareDeletionFailed'>
    | StatusNotificationBase<'shareCreationFailed'>
    | StatusNotificationBase<'filesWereShared'>
    | StatusNotificationBase<'filesDownloadFailed'>
    | StatusNotificationBase<'linkWasCopied'>
    | StatusNotificationBase<'purchaseSuccessful'>
    | StatusNotificationBase<'purchaseFailed'>
    | StatusNotificationBase<'planCanceled'>
    | StatusNotificationBase<'planCancelFailed'>
    | StatusNotificationBase<'planChangeSucceeded'>
    | StatusNotificationBase<'planChangeFailed'>
    | StatusNotificationBase<'planReactivated'>
    | StatusNotificationBase<'planReactivationFailed'>
    | (StatusNotificationBase<'stripeSetupIntentCreated'> & { email: string })
    | StatusNotificationBase<'changeProfilePictureFailed'>
    | StatusNotificationBase<'maxSelectionCountReached'>
    | StatusNotificationBase<'imageCopiedToClipboard'>
    | StatusNotificationBase<'imageCopyToClipboardFailed'>
    | StatusNotificationBase<'albumChangedToPrivate'>
    | StatusNotificationBase<'userNameChangeFailed'>
    | StatusNotificationBase<'deleteCardSuccess'>
    | StatusNotificationBase<'deleteCardFailed'>
    | (StatusNotificationBase<'trashAlbumsRestoreSuccess'> & {
          singular: boolean
      })
    | StatusNotificationBase<'trashAlbumsRestoreFailed'>
    | (StatusNotificationBase<'trashAlbumsDeleteSuccess'> & {
          singular: boolean
      })
    | StatusNotificationBase<'trashAlbumsDeleteFailed'>
    | StatusNotificationBase<'emailSubscribeSettingsChanged'>
    | PendingNotification

export type StatusNotificationState = {
    _nextID: number
    messages: StatusNotification[]
}

const initialState: StatusNotificationState = {
    _nextID: 0,
    messages: [],
}

const appendStatusNotification = (state: StatusNotificationState, msg: StatusNotification) => {
    return {
        ...state,
        _nextID: state._nextID + 1,
        messages: state.messages.concat(msg),
    }
}

const removePendingStatusNotificaton = (
    state: StatusNotificationState,
    pendingType: PendingStatusType,
) => {
    const pendingMsgIndex = getElementIndex(state.messages, (m) => m.type === pendingType)
    return {
        ...state,
        messages: state.messages.filter((_m, i) => i !== pendingMsgIndex),
    }
}

export const statusNotificationsReducer = (
    state: StatusNotificationState = initialState,
    action: Action,
): StatusNotificationState => {
    if (FileWasSetAsCoverPhoto.match(action)) {
        return appendStatusNotification(state, {
            id: state._nextID,
            type: 'albumCoverPhotoSet',
            relatedFile: action.payload.fileID,
        })
    }
    if (SetFileAsCoverPhotoFailed.match(action)) {
        return appendStatusNotification(state, {
            id: state._nextID,
            type: 'setAlbumCoverPhotoFailed',
            relatedJobID: action.payload.jobID,
            relatedFile: action.payload.fileID,
        })
    }

    if (LongRunningTaskStarted.match(action)) {
        return appendStatusNotification(state, {
            id: state._nextID,
            type: action.payload,
        })
    }

    if (LongRunningTaskFinished.match(action)) {
        return removePendingStatusNotificaton(state, action.payload)
    }

    if (FilesDeletionSucceeded.match(action)) {
        return appendStatusNotification(state, {
            id: state._nextID,
            type: 'filesDeleted',
            relatedJob: action.payload.jobID,
            relatedFiles: action.payload.files,
        })
    }
    if (FilesDeletionFailed.match(action)) {
        return appendStatusNotification(state, {
            id: state._nextID,
            type: 'filesDeletionFailed',
            relatedJob: action.payload.jobID,
            relatedFiles: action.payload.files,
            options: {
                supressRetry: action.payload.supressRetry,
            },
        })
    }

    if (TrashFilesDeletionSucceeded.match(action)) {
        return appendStatusNotification(state, {
            id: state._nextID,
            type: 'trashFilesDeleted',
            numFiles: action.payload.length,
        })
    }
    if (TrashFilesDeletionFailed.match(action)) {
        return appendStatusNotification(state, {
            id: state._nextID,
            type: 'trashFilesDeletionFailed',
            relatedFiles: action.payload,
        })
    }

    if (StatusNotificationDismissed.match(action)) {
        return {
            ...state,
            messages: state.messages.filter((m) => m.id !== action.payload),
        }
    }

    if (JobCopiedToTimeline.match(action)) {
        return appendStatusNotification(state, {
            id: state._nextID,
            type: 'jobCopiedToTimeline',
        })
    }

    if (JobCopiedToTimelineFailed.match(action)) {
        return appendStatusNotification(state, {
            id: state._nextID,
            type: 'jobCopiedToTimelineFailed',
        })
    }

    if (FilesCopiedToTimeline.match(action)) {
        return appendStatusNotification(state, {
            id: state._nextID,
            type: 'filesCopiedToTimeline',
            relatedFiles: action.payload,
        })
    }

    if (FilesCopiedToTimelineFailed.match(action)) {
        return appendStatusNotification(state, {
            id: state._nextID,
            type: 'filesCopiedToTimelineFailed',
        })
    }

    if (FilesCopiedToAlbum.match(action)) {
        return appendStatusNotification(state, {
            id: state._nextID,
            type: 'filesCopiedToAlbum',
            relatedJob: action.payload.jobID,
            relatedFiles: action.payload.files,
            showAlbumLink: action.payload.showAlbumLink === true,
        })
    }

    if (FilesCopiedToAlbumFailed.match(action)) {
        return appendStatusNotification(state, {
            id: state._nextID,
            type: 'filesCopiedToAlbumFailed',
            relatedJob: action.payload.jobID,
            relatedFiles: action.payload.files,
        })
    }
    if (JobDeletionFailed.match(action)) {
        return appendStatusNotification(state, {
            id: state._nextID,
            type: 'deleteAlbumFailed',
            relatedJobID: action.payload,
        })
    }
    if (AlbumUnsubscriptionFailed.match(action)) {
        return appendStatusNotification(state, {
            id: state._nextID,
            type: 'unsubscribeAlbumFailed',
            relatedJobID: action.payload,
        })
    }

    if (ShareCopiedToTimeline.match(action)) {
        return appendStatusNotification(state, {
            id: state._nextID,
            type: 'shareCopiedToTimeline',
        })
    }
    if (ShareCopiedToTimelineFailed.match(action)) {
        return appendStatusNotification(state, {
            id: state._nextID,
            type: 'shareCopiedToTimelineFailed',
        })
    }

    if (DeleteShareFailed.match(action)) {
        return appendStatusNotification(state, {
            id: state._nextID,
            type: 'shareDeletionFailed',
        })
    }

    if (ShareCreationFailed.match(action)) {
        return appendStatusNotification(state, {
            id: state._nextID,
            type: 'shareCreationFailed',
        })
    }

    if (FilesWereShared.match(action)) {
        return appendStatusNotification(state, {
            id: state._nextID,
            type: 'filesWereShared',
        })
    }

    if (
        FilesDownloadFailed.match(action) ||
        DownloadAlbumFailed.match(action) ||
        JobDownloadFailed.match(action)
    ) {
        return appendStatusNotification(state, {
            id: state._nextID,
            type: 'filesDownloadFailed',
        })
    }

    if (FilesRestorationSucceeded.match(action)) {
        return appendStatusNotification(state, {
            id: state._nextID,
            type: 'filesWereRestored',
            numFiles: action.payload.files.length,
        })
    }

    if (FilesRestorationFailed.match(action)) {
        return appendStatusNotification(state, {
            id: state._nextID,
            type: 'filesRestorationFailed',
            relatedJob: action.payload.jobID,
            relatedFiles: action.payload.files,
        })
    }
    if (AlbumLinkWasCopied.match(action)) {
        return appendStatusNotification(state, {
            id: state._nextID,
            type: 'linkWasCopied',
        })
    }
    if (AutoGeneratedAlbumFinished.match(action)) {
        return appendStatusNotification(state, {
            id: state._nextID,
            type: 'autoCreatedAlbum',
            relatedJobID: action.payload.jobID,
            relatedJobName: action.payload.name,
        })
    }
    if (PurchaseSucceeded.match(action)) {
        return appendStatusNotification(state, {
            id: state._nextID,
            type: 'purchaseSuccessful',
        })
    }
    if (PurchaseFailed.match(action)) {
        return appendStatusNotification(state, {
            id: state._nextID,
            type: 'purchaseFailed',
        })
    }
    if (PlanCanceled.match(action)) {
        return appendStatusNotification(state, {
            id: state._nextID,
            type: 'planCanceled',
        })
    }
    if (PlanCancelFailed.match(action)) {
        return appendStatusNotification(state, {
            id: state._nextID,
            type: 'planCancelFailed',
        })
    }
    if (PlanChangeSucceeded.match(action)) {
        return appendStatusNotification(state, {
            id: state._nextID,
            type: 'planChangeSucceeded',
        })
    }
    if (PlanChangeFailed.match(action)) {
        return appendStatusNotification(state, {
            id: state._nextID,
            type: 'planChangeFailed',
        })
    }
    if (PlanReactivated.match(action)) {
        return appendStatusNotification(state, {
            id: state._nextID,
            type: 'planReactivated',
        })
    }
    if (PlanReactivationFailed.match(action)) {
        return appendStatusNotification(state, {
            id: state._nextID,
            type: 'planReactivationFailed',
        })
    }
    if (StripeSetupIntentCreated.match(action)) {
        return appendStatusNotification(state, {
            id: state._nextID,
            type: 'stripeSetupIntentCreated',
            email: action.payload,
        })
    }
    if (ProfilePictureChangeFailed.match(action)) {
        return appendStatusNotification(state, {
            id: state._nextID,
            type: 'changeProfilePictureFailed',
        })
    }
    if (MaxSelectionCountReached.match(action)) {
        const alreadyExist = state.messages.some((m) => m.type === 'maxSelectionCountReached')
        if (!alreadyExist) {
            return appendStatusNotification(state, {
                id: state._nextID,
                type: 'maxSelectionCountReached',
            })
        }
    }
    if (imageCopiedToClipboard.match(action)) {
        return appendStatusNotification(state, {
            id: state._nextID,
            type: 'imageCopiedToClipboard',
        })
    }
    if (imageCopyToClipboardFailed.match(action)) {
        return appendStatusNotification(state, {
            id: state._nextID,
            type: 'imageCopyToClipboardFailed',
        })
    }
    if (AlbumChangedToPrivate.match(action)) {
        return appendStatusNotification(state, {
            id: state._nextID,
            type: 'albumChangedToPrivate',
        })
    }
    if (ProfileNameChangeFailed.match(action)) {
        return appendStatusNotification(state, {
            id: state._nextID,
            type: 'userNameChangeFailed',
        })
    }
    if (TrashAlbumsRestoreSuccess.match(action)) {
        return appendStatusNotification(state, {
            id: state._nextID,
            type: 'trashAlbumsRestoreSuccess',
            singular: action.payload.length === 1,
        })
    }
    if (TrashAlbumsRestoreFailed.match(action)) {
        return appendStatusNotification(state, {
            id: state._nextID,
            type: 'trashAlbumsRestoreFailed',
        })
    }
    if (TrashAlbumsDeleteSuccess.match(action)) {
        return appendStatusNotification(state, {
            id: state._nextID,
            type: 'trashAlbumsDeleteSuccess',
            singular: action.payload.length === 1,
        })
    }
    if (TrashAlbumsDeleteFailed.match(action)) {
        return appendStatusNotification(state, {
            id: state._nextID,
            type: 'trashAlbumsDeleteFailed',
        })
    }
    if (EmailSubscribeSettingsChanged.match(action)) {
        return appendStatusNotification(state, {
            id: state._nextID,
            type: 'emailSubscribeSettingsChanged',
        })
    }
    return state
}

export const statusNotificationsReducerMapObj = {
    statusNotifications: statusNotificationsReducer,
}

export type StateWithStatusNotifications = StateOfReducerMapObj<
    typeof statusNotificationsReducerMapObj
>
